import * as React from "react";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { TemplateCheckResult } from "../documentAuthoring.types";
import type { CheckStatus } from "../hooks/useAuthoringRun";
import { countEditKinds, plural } from "./helpers";
import { FailedEditRow } from "./RunReport";

export interface TemplateCheck {
  heading: string;
  prompt: string;
}

interface Props {
  checks: TemplateCheck[];
  statuses: CheckStatus[];
  results: TemplateCheckResult[] | null;
}

const StatusBadge: React.FC<{ status: CheckStatus; result?: TemplateCheckResult }> = ({ status, result }) => {
  switch (status) {
    case "running":
      return (
        <Badge variant="review-neutral" className="gap-1" data-testid="check-status">
          <CircleLoader fill="#6b7280" />
          Checking
        </Badge>
      );
    case "reviewed":
      return (
        <Badge variant="review-neutral" data-testid="check-status">
          Reviewed
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="review-red" data-testid="check-status">
          Couldn't run
        </Badge>
      );
    case "done":
      return (
        <Badge variant={result?.applied.length ? "traffic-green" : "review-neutral"} data-testid="check-status">
          {result ? countEditKinds(result.applied) : "Done"}
        </Badge>
      );
    default:
      return null;
  }
};

/** Per-check rows with status badge, summary, failed edits and a collapsible prompt (screenshot layout). */
export const TemplateRunReport: React.FC<Props> = ({ checks, statuses, results }) => (
  <ol className="space-y-2" aria-label="Template checks">
    {checks.map((check, index) => {
      const status = statuses[index] ?? "idle";
      const result = results?.[index];
      return (
        <li
          key={`${index}-${check.heading}`}
          className={cn(
            "rounded-md border border-desert-sand-300 bg-white p-3 shadow-sm",
            status === "failed" && "border-red-200",
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-ui-heading-text">{check.heading}</h3>
            <StatusBadge status={status} result={result} />
          </div>
          {result?.summary && <p className="mt-2 text-xs text-ui-paragraph-text">{result.summary}</p>}
          {result?.error && (
            <p className="mt-2 text-xs text-danger" role="alert">
              {result.error}
            </p>
          )}
          {result && result.failed.length > 0 && (
            <div className="mt-2 space-y-1 rounded bg-amber-50 p-2">
              <p className="text-xxs font-semibold text-amber-900">
                {plural(result.failed.length, "edit couldn't", "edits couldn't")} be placed
              </p>
              <ul className="space-y-1">
                {result.failed.map((edit, failedIndex) => (
                  <FailedEditRow key={failedIndex} edit={edit} />
                ))}
              </ul>
            </div>
          )}
          {result ? (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-gray-500">Prompt</summary>
              <p className="mt-1 whitespace-pre-wrap text-gray-600">{check.prompt}</p>
            </details>
          ) : (
            <p className="mt-2 whitespace-pre-wrap text-xs text-gray-600">{check.prompt}</p>
          )}
        </li>
      );
    })}
  </ol>
);
